// ---------------------------------------------------------------------------
// AppLock — Record-level IndexedDB encryption adapter
// ---------------------------------------------------------------------------
//
// Works with the consuming app's EXISTING IndexedDB database and object
// stores. Instead of owning its own key-value store, this adapter
// encrypts/decrypts individual records in-place:
//
//   Original record:   { id: 'abc', title: 'My Recipe', steps: [...] }
//   Encrypted record:  { id: 'abc', _encrypted: '{"v":1,"iv":"...","ct":"..."}' }
//
// The keyPath field (e.g. `id`) stays in cleartext so IndexedDB key
// lookups still work. Everything else is encrypted into `_encrypted`.
//
// ---------------------------------------------------------------------------
import { encrypt, decrypt, isEncryptedPayload, } from '../crypto/crypto-adapter.js';
// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------
/**
 * Record-level IndexedDB encryption adapter.
 *
 * Opens the app's existing database (by name, without specifying a
 * version so no `onupgradeneeded` fires). The database schema is
 * owned by the consuming app — this adapter never modifies it.
 *
 * Key design decisions:
 * - All async crypto work is done BEFORE creating IDB transactions
 *   to avoid transaction auto-commit issues.
 * - The `onversionchange` handler closes AppLock's connection if the
 *   app triggers a schema upgrade, preventing blocking.
 * - keyPath is read from the object store metadata and cached.
 */
export class IDBRecordAdapter {
    constructor(keyLifecycle, config) {
        this.keyLifecycle = keyLifecycle;
        this.config = config;
        this.dbPromise = null;
        this.keyPathCache = new Map();
    }
    // -----------------------------------------------------------------------
    // IDBRecordAPI — CRUD
    // -----------------------------------------------------------------------
    async put(storeName, value) {
        const db = await this.openDb();
        const record = value;
        if (this.shouldEncrypt()) {
            const keyPath = await this.getKeyPath(storeName);
            const cryptoKey = this.keyLifecycle.get(); // throws if locked
            const serialized = JSON.stringify(record);
            const payload = await encrypt(cryptoKey, serialized);
            // Build the encrypted record: cleartext key + encrypted blob.
            // All async work is done — create the transaction now so it
            // doesn't auto-commit out from under us.
            const encryptedRecord = {
                [keyPath]: record[keyPath],
                _encrypted: JSON.stringify(payload),
            };
            return this.idbPut(db, storeName, encryptedRecord);
        }
        return this.idbPut(db, storeName, record);
    }
    async get(storeName, key) {
        const db = await this.openDb();
        const raw = await this.idbGet(db, storeName, key);
        if (raw === undefined)
            return undefined;
        if (this.shouldEncrypt() && this.isEncryptedRecord(raw)) {
            return this.decryptRecord(raw);
        }
        return raw;
    }
    async getAll(storeName) {
        const db = await this.openDb();
        const rawRecords = await this.idbGetAll(db, storeName);
        if (!this.shouldEncrypt()) {
            return rawRecords;
        }
        const results = [];
        for (const raw of rawRecords) {
            if (this.isEncryptedRecord(raw)) {
                results.push(await this.decryptRecord(raw));
            }
            else {
                // Cleartext record (pre-migration or non-encrypted store)
                results.push(raw);
            }
        }
        return results;
    }
    async delete(storeName, key) {
        const db = await this.openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            store.delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
    async clear(storeName) {
        const db = await this.openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            store.clear();
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
    async count(storeName) {
        const db = await this.openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const req = store.count();
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    }
    // -----------------------------------------------------------------------
    // Migration helpers (used by AppLockImpl)
    // -----------------------------------------------------------------------
    /**
     * Encrypt all existing cleartext records across all object stores.
     * Called during first-time `setup()`.
     */
    async migrateToEncrypted(key) {
        const db = await this.openDb();
        const storeNames = Array.from(db.objectStoreNames);
        for (const storeName of storeNames) {
            // Defensive: skip stores without a string keyPath (e.g., AppLock's own
            // flat KV store using out-of-line keys). Such stores cannot be record-encrypted.
            let keyPath;
            try {
                keyPath = await this.getKeyPath(storeName);
            }
            catch {
                continue;
            }
            const records = await this.idbGetAll(db, storeName);
            const encrypted = [];
            for (const raw of records) {
                if (this.isEncryptedRecord(raw))
                    continue; // already encrypted
                const record = raw;
                const serialized = JSON.stringify(record);
                const payload = await encrypt(key, serialized);
                encrypted.push({
                    [keyPath]: record[keyPath],
                    _encrypted: JSON.stringify(payload),
                });
            }
            if (encrypted.length > 0) {
                await this.idbPutBatch(db, storeName, encrypted);
            }
        }
    }
    /**
     * Decrypt all encrypted records back to cleartext.
     * Called during `removePassword()`.
     */
    async migrateToCleartext(key) {
        const db = await this.openDb();
        const storeNames = Array.from(db.objectStoreNames);
        for (const storeName of storeNames) {
            // Defensive: skip stores without a string keyPath.
            try {
                await this.getKeyPath(storeName);
            }
            catch {
                continue;
            }
            const records = await this.idbGetAll(db, storeName);
            const decrypted = [];
            for (const raw of records) {
                if (!this.isEncryptedRecord(raw))
                    continue;
                const record = raw;
                const payloadStr = record._encrypted;
                const payload = JSON.parse(payloadStr);
                const plaintext = await decrypt(key, payload);
                decrypted.push(JSON.parse(plaintext));
            }
            if (decrypted.length > 0) {
                await this.idbPutBatch(db, storeName, decrypted);
            }
        }
    }
    /**
     * Re-encrypt all records from an old key to a new key.
     * Called during `changePassword()`.
     */
    async reEncrypt(oldKey, newKey) {
        const db = await this.openDb();
        const storeNames = Array.from(db.objectStoreNames);
        for (const storeName of storeNames) {
            // Defensive: skip stores without a string keyPath.
            let keyPath;
            try {
                keyPath = await this.getKeyPath(storeName);
            }
            catch {
                continue;
            }
            const records = await this.idbGetAll(db, storeName);
            const reEncrypted = [];
            for (const raw of records) {
                if (!this.isEncryptedRecord(raw))
                    continue;
                const record = raw;
                // Decrypt with old key
                const payloadStr = record._encrypted;
                const payload = JSON.parse(payloadStr);
                const plaintext = await decrypt(oldKey, payload);
                const original = JSON.parse(plaintext);
                // Re-encrypt with new key
                const newPayload = await encrypt(newKey, JSON.stringify(original));
                reEncrypted.push({
                    [keyPath]: original[keyPath],
                    _encrypted: JSON.stringify(newPayload),
                });
            }
            if (reEncrypted.length > 0) {
                await this.idbPutBatch(db, storeName, reEncrypted);
            }
        }
    }
    // -----------------------------------------------------------------------
    // Lifecycle
    // -----------------------------------------------------------------------
    /** Close the database connection. Called by `AppLock.destroy()`. */
    close() {
        if (this.dbPromise) {
            this.dbPromise.then((db) => db.close()).catch(() => {
                // Swallow — already closed or failed to open
            });
            this.dbPromise = null;
        }
        this.keyPathCache.clear();
    }
    // -----------------------------------------------------------------------
    // Internal — database access
    // -----------------------------------------------------------------------
    /**
     * Open the app's existing IndexedDB database.
     *
     * Opens WITHOUT specifying a version so `onupgradeneeded` never fires.
     * The database schema is fully owned by the consuming app.
     */
    openDb() {
        if (!this.dbPromise) {
            this.dbPromise = new Promise((resolve, reject) => {
                // No version → opens at current version, no schema changes
                const request = indexedDB.open(this.config.idbDatabaseName);
                request.onsuccess = () => {
                    const db = request.result;
                    // If the app triggers a schema upgrade, close our connection
                    // so we don't block it.
                    db.onversionchange = () => {
                        db.close();
                        this.dbPromise = null;
                        this.keyPathCache.clear();
                    };
                    resolve(db);
                };
                request.onerror = () => reject(request.error);
            });
        }
        return this.dbPromise;
    }
    /** Read and cache the keyPath for an object store. */
    async getKeyPath(storeName) {
        if (this.keyPathCache.has(storeName)) {
            return this.keyPathCache.get(storeName);
        }
        const db = await this.openDb();
        const tx = db.transaction(storeName, 'readonly');
        const keyPath = tx.objectStore(storeName).keyPath;
        if (typeof keyPath !== 'string') {
            throw new Error(`[AppLock] Object store "${storeName}" has a non-string keyPath ` +
                `(${JSON.stringify(keyPath)}). Only single-string keyPaths are supported.`);
        }
        this.keyPathCache.set(storeName, keyPath);
        return keyPath;
    }
    // -----------------------------------------------------------------------
    // Internal — IDB primitives (sync within transactions)
    // -----------------------------------------------------------------------
    idbPut(db, storeName, record) {
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            store.put(record);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
    idbGet(db, storeName, key) {
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const req = store.get(key);
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    }
    idbGetAll(db, storeName) {
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readonly');
            const store = tx.objectStore(storeName);
            const req = store.getAll();
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    }
    idbPutBatch(db, storeName, records) {
        return new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, 'readwrite');
            const store = tx.objectStore(storeName);
            for (const record of records) {
                store.put(record);
            }
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
    // -----------------------------------------------------------------------
    // Internal — encryption helpers
    // -----------------------------------------------------------------------
    shouldEncrypt() {
        return this.config.mode === 'encrypted';
    }
    /** Check if a raw record is in the encrypted { keyPath, _encrypted } shape. */
    isEncryptedRecord(record) {
        if (!record || typeof record !== 'object')
            return false;
        const rec = record;
        if (typeof rec._encrypted !== 'string')
            return false;
        return isEncryptedPayload(rec._encrypted);
    }
    /** Decrypt a record's `_encrypted` field and return the original object. */
    async decryptRecord(record) {
        const cryptoKey = this.keyLifecycle.get(); // throws if locked
        const payloadStr = record._encrypted;
        const payload = JSON.parse(payloadStr);
        const decrypted = await decrypt(cryptoKey, payload);
        return JSON.parse(decrypted);
    }
}
//# sourceMappingURL=idb-record-adapter.js.map